import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Languages } from "lucide-react";
import { Toaster, toast } from "sonner";
import { apiFetch } from "../lib/api";

const EMPTY = {
  title: "", vertical: "dental", channel: "coffee-dental", language: "ms",
  category: "", author: "Dr. Irma", slug: "", excerpt: "", body: "",
  seo_title: "", meta_description: "", featured_image: "", alt_text: "", status: "draft",
};

const inputCls = "w-full border border-ink/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber";

export default function Content() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [translating, setTranslating] = useState("");

  const load = useCallback(() => {
    apiFetch("/admin/content").then(setItems).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const translate = async (item) => {
    setTranslating(item.id);
    try {
      const res = await apiFetch(`/admin/content/${item.id}/translate`, { method: "POST" });
      if (!res.queued || res.queued.length === 0) {
        toast.info(res.message || "All translations already exist");
        return;
      }
      toast.success(`Translating into ${res.queued.join(" · ").toUpperCase()} — drafts will appear below shortly`);
      const target = items.length + res.queued.length;
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const fresh = await apiFetch("/admin/content");
          if (fresh.length >= target || Date.now() - started > 150000) {
            setItems(fresh);
            clearInterval(poll);
            if (fresh.length >= target) toast.success("Translations ready — review the drafts below");
          }
        } catch {}
      }, 8000);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTranslating("");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    let res;
    if (editingId) {
      res = await apiFetch(`/admin/content/${editingId}`, { method: "PUT", body: JSON.stringify(form) });
      toast.success("Content updated");
    } else {
      res = await apiFetch("/admin/content", { method: "POST", body: JSON.stringify(form) });
      toast.success("Content created");
    }
    if (res?.taxonomy_warning) toast.warning(res.taxonomy_warning, { duration: 8000 });
    setForm(EMPTY);
    setEditingId(null);
    setShowForm(false);
    load();
  };

  const toggleStatus = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    await apiFetch(`/admin/content/${item.id}`, { method: "PUT", body: JSON.stringify({ ...item, status: next }) });
    load();
  };

  const edit = (item) => {
    setForm({ ...EMPTY, ...item });
    setEditingId(item.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const remove = async (id) => {
    await apiFetch(`/admin/content/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div data-testid="admin-content">
      <Toaster position="top-center" richColors />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-2 font-serif text-3xl tracking-tight text-ink">Articles & pages</h1>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(!showForm); }} className="btn-primary !px-5 !py-2.5" data-testid="content-new-button">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> New Content
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="mt-8 grid gap-4 border border-ink/10 bg-warmwhite p-6 sm:grid-cols-2" data-testid="content-form">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${inputCls} sm:col-span-2`} data-testid="content-title-input" />
          <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} className={inputCls} data-testid="content-vertical-select">
            <option value="dental">Dental</option>
            <option value="consulting">Consulting</option>
            <option value="publishing">Publishing / Creative</option>
          </select>
          <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className={inputCls} data-testid="content-channel-select">
            <option value="coffee-dental">Coffee · Dental</option>
            <option value="coffee-growth">Coffee · Growth</option>
            <option value="coffee-stories">Coffee · Stories</option>
            <option value="page">Standalone page</option>
          </select>
          <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className={inputCls} data-testid="content-language-select">
            <option value="ms">Bahasa Melayu</option>
            <option value="en">English</option>
            <option value="zh">中文</option>
            <option value="ta">தமிழ்</option>
            <option value="id">Bahasa Indonesia</option>
          </select>
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="content-category-input" />
          <input placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputCls} data-testid="content-author-input" />
          <input placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputCls} data-testid="content-excerpt-input" />
          <textarea placeholder="Body" rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inputCls} sm:col-span-2`} data-testid="content-body-input" />
          <input placeholder="SEO title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className={inputCls} data-testid="content-seo-title-input" />
          <input placeholder="Meta description" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className={inputCls} data-testid="content-meta-input" />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="content-status-select">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
          </select>
          <div className="flex items-center gap-4">
            <button type="submit" className="btn-primary !px-5 !py-2.5" data-testid="content-save-button">
              {editingId ? "Update" : "Create"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY); }} className="text-xs uppercase tracking-[0.2em] text-ink/50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-10 space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex flex-wrap items-center gap-4 border border-ink/10 bg-warmwhite p-5" data-testid={`content-row-${item.slug}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-ink">{item.title}</p>
              <p className="mt-1 text-xs text-ink/50">
                {item.vertical} · {item.channel || "page"} · {item.language} · by {item.author}
              </p>
            </div>
            <span className={`px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.15em] ${item.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-softgrey text-ink/60"}`} data-testid={`content-status-${item.slug}`}>
              {item.status}
            </span>
            <button onClick={() => translate(item)} disabled={translating === item.id} className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink/70 hover:border-amber disabled:opacity-50" data-testid={`content-translate-${item.slug}`}>
              <Languages className="h-3.5 w-3.5" strokeWidth={1.5} /> {translating === item.id ? "Translating…" : "Translate"}
            </button>
            <button onClick={() => toggleStatus(item)} className="border border-ink/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink/70 hover:border-amber" data-testid={`content-toggle-${item.slug}`}>
              {item.status === "published" ? "Unpublish" : "Publish"}
            </button>
            <button onClick={() => edit(item)} className="text-ink/40 hover:text-burgundy" aria-label="Edit" data-testid={`content-edit-${item.slug}`}>
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button onClick={() => remove(item.id)} className="text-ink/40 hover:text-red-600" aria-label="Delete" data-testid={`content-delete-${item.slug}`}>
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
